/**
 * WEB COPY of IdleLockSettingsScreen.tsx — picked by the web bundler ahead of
 * the mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * Same options, same storage (idleLockPreferences.web.ts supplies the web
 * default). Only the wording differs: on the web the lock is lifted with
 * the account password, not Face ID or Touch ID, and there is no app
 * "background" — the timer counts from the last click or keystroke in the
 * tab. When IdleLockSettingsScreen.tsx changes, re-apply the change here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  SafeAreaView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { NavigationProp } from '@react-navigation/native';

import {
  DEFAULT_IDLE_LOCK_THRESHOLD_MS,
  getIdleLockThreshold,
  IDLE_LOCK_OPTIONS,
  setIdleLockThreshold,
} from '../../services/auth/idleLockPreferences';
import { createLogger } from '../../utils/logger';
import { ms, s, vs } from '../../utils/responsive';

import backIcon from '../../assets/icons/back.png';

const logger = createLogger('IdleLockSettings');

interface Props {
  navigation: NavigationProp<any>;
}

const IdleLockSettingsScreen: React.FC<Props> = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<number>(
    DEFAULT_IDLE_LOCK_THRESHOLD_MS,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const current = await getIdleLockThreshold();
        if (!cancelled) setSelected(current);
      } catch (err) {
        logger.error('Failed to load idle-lock threshold');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = useCallback(async (ms: number) => {
    setSaving(true);
    const previous = selected;
    setSelected(ms);
    try {
      await setIdleLockThreshold(ms);
    } catch (err) {
      logger.error('Failed to save idle-lock threshold');
      setSelected(previous);
    } finally {
      setSaving(false);
    }
  }, [selected]);

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image source={backIcon} style={styles.backIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Auto-Lock Timer</Text>
        <View style={{ width: ms(24) }} />
      </View>

      <View style={styles.content}>
        <Text style={styles.description}>
          When you haven't clicked or typed for longer than this, the app
          covers your vault and asks for your password before showing it
          again. This is a privacy screen for a shared or unattended
          computer, not a sign-out: your vault stays unlocked underneath
          until you sign out or close the tab.
        </Text>

        {loading ? (
          <ActivityIndicator style={{ marginTop: vs(24) }} color="#29BFC0" />
        ) : (
          <View style={styles.optionList}>
            {IDLE_LOCK_OPTIONS.map((option) => {
              const isSelected = option.ms === selected;
              return (
                <TouchableOpacity
                  key={option.ms}
                  style={[
                    styles.option,
                    isSelected && styles.optionSelected,
                  ]}
                  disabled={saving}
                  onPress={() => handleSelect(option.ms)}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: isSelected }}
                >
                  <Text
                    style={[
                      styles.optionLabel,
                      isSelected && styles.optionLabelSelected,
                    ]}
                  >
                    {option.label}
                  </Text>
                  <View
                    style={[
                      styles.radio,
                      isSelected && styles.radioSelected,
                    ]}
                  >
                    {isSelected ? <View style={styles.radioInner} /> : null}
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: s(16),
    paddingVertical: vs(12),
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  backButton: {
    padding: s(4),
  },
  backIcon: {
    width: ms(24),
    height: ms(24),
    resizeMode: 'contain',
  },
  headerTitle: {
    fontSize: ms(16),
    fontWeight: '600',
    color: '#1A1A1A',
  },
  content: {
    paddingHorizontal: s(16),
    paddingTop: vs(16),
  },
  description: {
    fontSize: ms(13),
    color: '#5A5A5A',
    lineHeight: ms(20),
    marginBottom: vs(16),
  },
  optionList: {
    borderTopWidth: 1,
    borderColor: '#EEEEEE',
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: vs(14),
    paddingHorizontal: s(8),
    borderBottomWidth: 1,
    borderColor: '#EEEEEE',
  },
  optionSelected: {
    backgroundColor: '#F3FBFB',
  },
  optionLabel: {
    fontSize: ms(15),
    color: '#1A1A1A',
  },
  optionLabelSelected: {
    fontWeight: '600',
    color: '#29BFC0',
  },
  radio: {
    width: ms(22),
    height: ms(22),
    borderRadius: ms(11),
    borderWidth: 2,
    borderColor: '#CCCCCC',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelected: {
    borderColor: '#29BFC0',
  },
  radioInner: {
    width: ms(10),
    height: ms(10),
    borderRadius: ms(5),
    backgroundColor: '#29BFC0',
  },
});

export default IdleLockSettingsScreen;
